import { motion } from "framer-motion";
import { Heart, Swords, Shield } from "lucide-react";
import GlitchText from "./GlitchText";
import { GENRE_ASTRYX, GENRE_ZENITH, GENRE_VANTA } from "./assets";
import { useLang } from "./LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const META = [
  { name: "Astryx", key: "astryx", roleKey: "health", icon: Heart, logo: GENRE_ASTRYX, color: "#2DD4A7" },
  { name: "Zenith", key: "zenith", roleKey: "offense", icon: Swords, logo: GENRE_ZENITH, color: "#FF6A2B" },
  { name: "Vanta", key: "vanta", roleKey: "defense", icon: Shield, logo: GENRE_VANTA, color: "#B57BFF" },
];

export const Genres = () => {
  const { t } = useLang();
  return (
    <section id="genres" data-testid="genres-section" className="relative py-24 md:py-32 bg-[#080D1A] border-y border-[#00F2FE]/10 overflow-hidden">
      <div className="absolute inset-0 stars opacity-30" />
      <div className="absolute inset-0 asanoha opacity-50" />
      <span aria-hidden="true" className="pointer-events-none select-none absolute top-10 left-1/2 -translate-x-1/2 font-display text-[14rem] leading-none text-white/[0.025]">闘</span>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16 text-center">
          <p className="text-[#F9D423] text-xs font-bold tracking-[0.3em] mb-4">{t("genres.overline")}</p>
          <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-tight">
            {t("genres.headingPre")} <GlitchText className="glitch-title text-[#00F2FE]">{t("genres.headingGlitch")}</GlitchText>
          </h2>
          <p className="mt-5 text-slate-400 text-sm md:text-base max-w-xl mx-auto">{t("genres.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {META.map((g, i) => {
            const Icon = g.icon;
            return (
              <motion.div
                key={g.name}
                data-testid={`genre-card-${i}`}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease, delay: i * 0.1 }}
                className="group relative rounded-sm overflow-hidden border p-8 md:p-10 text-center transition-all duration-500 hover:-translate-y-2"
                style={{ borderColor: `${g.color}40`, background: "linear-gradient(180deg, rgba(12,19,38,0.85), rgba(6,10,20,0.95))" }}
              >
                <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-56 h-56 rounded-full opacity-25 blur-[90px] transition-opacity duration-500 group-hover:opacity-50" style={{ background: g.color }} />
                <div className="relative flex items-center justify-center h-44 mb-6">
                  <img src={g.logo} alt={`${g.name} logo`} className="max-h-44 w-auto object-contain transition-transform duration-500 group-hover:scale-105" style={{ filter: `drop-shadow(0 0 26px ${g.color}88)` }} />
                </div>
                <div className="relative flex items-center justify-center gap-2 mb-2" style={{ color: g.color }}>
                  <Icon size={16} />
                  <span className="font-mono-x text-[11px] tracking-[0.3em] uppercase">{t(`genres.roles.${g.roleKey}`)}</span>
                </div>
                <h3 className="relative font-display font-bold uppercase text-3xl text-white mb-4">{g.name}</h3>
                <p className="relative text-slate-400 text-sm leading-relaxed max-w-xs mx-auto">{t(`genres.desc.${g.key}`)}</p>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-sm" style={{ boxShadow: `inset 0 0 50px ${g.color}33`, border: `1px solid ${g.color}` }} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Genres;
